import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useFormTracking } from '../../hooks/useAnalytics';

const BusinessInfoStep = ({ data, updateData, onNext, onBack }) => {
  const { 
    trackStepStart, 
    trackStepCompleted, 
    trackFieldInteraction,
    trackValidationFailure,
    trackSpecialFieldSelection 
  } = useOnboarding();
  
  const { 
    trackFormStart,
    trackFieldFocus, 
    trackFieldChange,
    trackFormSubmit 
  } = useFormTracking('onboarding_step_2_business');
  
  const [formData, setFormData] = useState({
    companyName: data.companyName || '',
    industry: data.industry || '',
    industryOther: data.industryOther || '',
    companySize: data.companySize || ''
  });
  const [errors, setErrors] = useState({});
  const [showOtherIndustry, setShowOtherIndustry] = useState(false);
  const fieldRefs = useRef({});
  const stepStartTime = useRef(Date.now());

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'E-commerce',
    'Real Estate',
    'Marketing/Advertising',
    'Customer Service',
    'HR/Recruiting',
    'Legal',
    'Other'
  ];
  
  // Track step start on mount
  useEffect(() => {
    trackStepStart(2, 'company_info');
    trackFormStart();
    
    // Check if industry is "Other" and show input
    if (data.industry === 'Other') {
      setShowOtherIndustry(true);
    }
  }, [trackStepStart, trackFormStart, data.industry]);

  const companySizes = [
    { value: 'solo', label: 'Solo/Freelancer (1)', description: 'Just getting started' },
    { value: 'small', label: 'Small Team (2-10)', description: 'Close-knit team' },
    { value: 'growing', label: 'Growing Business (11-50)', description: 'Expanding operations' },
    { value: 'mid-size', label: 'Mid-size Company (51-200)', description: 'Established business' },
    { value: 'large', label: 'Large Enterprise (201-1000)', description: 'Multiple departments' },
    { value: 'enterprise', label: 'Enterprise (1000+)', description: 'Global organization' }
  ];

  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    updateData(newFormData);
    
    // Track field interaction
    trackFieldInteraction(2, field, 'change', value);
    trackFieldChange(field, value);
    
    // Clear error when user starts typing/selecting
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Handle special "Other" selection for industry
    if (field === 'industry') {
      const isOther = value === 'Other';
      setShowOtherIndustry(isOther);
      
      if (!isOther) {
        // Clear other field if not selected
        const updatedData = { ...newFormData, industryOther: '' };
        setFormData(updatedData);
        updateData(updatedData);
      }
      
      // Track special selection
      trackSpecialFieldSelection('industry', value, isOther);
    }
  };
  
  /**
   * Handle field focus with analytics
   */
  const handleFieldFocus = (field) => {
    trackFieldInteraction(2, field, 'focus');
    trackFieldFocus(field);
  };
  
  /**
   * Handle field blur with analytics
   */
  const handleFieldBlur = (field, value) => {
    trackFieldInteraction(2, field, 'blur', value);
    
    // Track specific events for key fields
    if (field === 'companyName' && value) {
      trackFieldInteraction(2, 'company_name_entered', 'completed', value);
    } else if (field === 'companySize' && value) {
      trackFieldInteraction(2, 'company_size_selected', 'completed', value);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let validationFailures = [];
    
    // Validate company name
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company/Business name is required';
      validationFailures.push('companyName');
    } else if (formData.companyName.trim().length < 2) {
      newErrors.companyName = 'Please enter a valid company name';
      validationFailures.push('companyName');
    }
    
    // Validate industry
    if (!formData.industry) {
      newErrors.industry = 'Please select your industry';
      validationFailures.push('industry');
    }
    
    // Validate "Other" industry input if selected
    if (formData.industry === 'Other' && !formData.industryOther.trim()) {
      newErrors.industryOther = 'Please specify your industry';
      validationFailures.push('industryOther');
    }
    
    // Validate company size
    if (!formData.companySize) {
      newErrors.companySize = 'Please select your company size';
      validationFailures.push('companySize');
    }
    
    // Track validation failures
    validationFailures.forEach(field => {
      trackValidationFailure(2, field, newErrors[field]);
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      const timeSpent = Math.round((Date.now() - stepStartTime.current) / 1000);
      
      // Track step completion
      trackStepCompleted(2, 'company_info');
      
      // Track form submission
      trackFormSubmit(true);
      
      // Track collected data
      const fieldsCompleted = [
        formData.companyName ? 'companyName' : null,
        formData.industry ? 'industry' : null,
        formData.industryOther ? 'industryOther' : null,
        formData.companySize ? 'companySize' : null
      ].filter(Boolean);
      
      trackFieldInteraction(2, 'step_completed', 'submit', {
        fields_completed: fieldsCompleted,
        time_spent: timeSpent
      });
      
      onNext();
    } else {
      // Track form submission failure
      trackFormSubmit(false, Object.keys(errors));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNext();
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-bold text-card-foreground">
          Tell us about your business
        </h2>
        <p className="text-sm text-muted-foreground">
          This helps us customize the AI agent recommendations for your specific industry and company size.
        </p>
      </div>

      <div className="flex flex-col gap-6">
        {/* Company Name */}
        <div className="space-y-2">
          <Label htmlFor="companyName" className="text-card-foreground">
            Company/Business Name *
          </Label>
          <Input
            id="companyName"
            ref={el => fieldRefs.current.companyName = el}
            type="text"
            placeholder="Enter your company or business name"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            onFocus={() => handleFieldFocus('companyName')}
            onBlur={(e) => handleFieldBlur('companyName', e.target.value)}
            onKeyPress={handleKeyPress}
            className={errors.companyName ? 'border-red-500' : ''}
            autoComplete="organization"
          />
          {errors.companyName && (
            <p className="text-sm text-red-600">{errors.companyName}</p>
          )}
        </div>

        {/* Industry */}
        <div className="space-y-2">
          <Label htmlFor="industry" className="text-card-foreground">
            Industry *
          </Label>
          <Select
            value={formData.industry}
            onValueChange={(value) => handleInputChange('industry', value)}
            onOpenChange={(open) => {
              if (open) handleFieldFocus('industry');
              else handleFieldBlur('industry', formData.industry);
            }}
          >
            <SelectTrigger 
              id="industry"
              className={errors.industry ? 'border-red-500' : ''}
            >
              <SelectValue placeholder="Select your industry" />
            </SelectTrigger>
            <SelectContent>
              {industries.map((industry) => (
                <SelectItem key={industry} value={industry}>
                  {industry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.industry && (
            <p className="text-sm text-red-600">{errors.industry}</p>
          )}
        </div>
        
        {/* Other Industry Input (Conditional) */}
        {showOtherIndustry && (
          <div className="space-y-2">
            <Label htmlFor="industryOther" className="text-card-foreground">
              Please specify your industry *
            </Label>
            <Input
              id="industryOther"
              ref={el => fieldRefs.current.industryOther = el}
              type="text"
              placeholder="Please tell us about your industry..."
              value={formData.industryOther}
              onChange={(e) => handleInputChange('industryOther', e.target.value)}
              onFocus={() => handleFieldFocus('industryOther')}
              onBlur={(e) => handleFieldBlur('industryOther', e.target.value)}
              onKeyPress={handleKeyPress}
              className={errors.industryOther ? 'border-red-500' : ''}
            />
            {errors.industryOther && (
              <p className="text-sm text-red-600">{errors.industryOther}</p>
            )}
          </div>
        )}

        {/* Company Size */}
        <div className="space-y-2">
          <Label htmlFor="companySize" className="text-card-foreground">
            Company/Business Size *
          </Label>
          <Select
            value={formData.companySize}
            onValueChange={(value) => handleInputChange('companySize', value)}
            onOpenChange={(open) => {
              if (open) handleFieldFocus('companySize');
              else handleFieldBlur('companySize', formData.companySize);
            }}
          >
            <SelectTrigger 
              id="companySize"
              className={errors.companySize ? 'border-red-500' : ''}
            >
              <SelectValue placeholder="Select your company size" />
            </SelectTrigger>
            <SelectContent>
              {companySizes.map((size) => (
                <SelectItem key={size.value} value={size.value}>
                  <span className="font-medium">{size.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.companySize && (
            <p className="text-sm text-red-600">{errors.companySize}</p>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center pt-6">
        <Button
          onClick={onBack}
          variant="outline"
          className="px-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <Button
          onClick={handleNext}
          className="px-6"
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default BusinessInfoStep;